import { useState } from 'react';
import { Box, Button, Chip, IconButton, Radio, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { KeyValueList } from '../../components/KeyValueList';
import { Panel } from '../../components/Panel';
import { StatusChip, StatusDot } from '../../components/StatusChip';
import { TabStrip } from '../../components/TabStrip';
import { useNotice } from '../../components/NoticeProvider';
import { margin, moneyOrDash } from '../../lib/money';
import { useDocuments } from '../documents/useDocuments';
import { ModeIcon, RouteText } from '../shipments/ShipmentCard';
import { CargoPanel, DocumentsPanel, PartiesPanel, RoutePanel } from '../shipments/ShipmentPanels';
import { detailOf } from '../shipments/sample';
import { StageStepper } from '../shipments/StageStepper';
import { NEXT_ACTION, QUOTE_STEPS, QUOTE_TONE, quoteStep, type Quotation } from './sample';
import { useQuotations } from './useQuotations';

const TABS = ['Overview', 'Prices & Options', 'Email Content', 'Documents', 'History'] as const;

/** One quotation in full, shown in the panel that slides in from the right */
export function QuotationDetail({ q, onClose }: { q: Quotation; onClose: () => void }) {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const docs = useDocuments();
  const s = useQuotations().shipmentOf(q);
  const d = detailOf(s);

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2.5 }, pt: 'max(16px, env(safe-area-inset-top))' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h2">
              {q.srb}
            </Typography>
            <StatusChip label={q.status} tone={QUOTE_TONE[q.status]} />
          </Stack>
          <Typography sx={{ fontWeight: 650 }}>{s.customer}</Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}>
        <Chip size="small" variant="outlined" icon={<ModeIcon mode={s.mode} />} label={s.service} />
        <Chip size="small" variant="outlined" label={s.cargo} />
        <Chip size="small" variant="outlined" label={s.incoterm} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          <RouteText s={s} />
        </Typography>
      </Stack>

      <StageStepper steps={QUOTE_STEPS} current={quoteStep(q.status)} dates={[]} />
      <TabStrip tabs={TABS.map((t) => ({ value: t }))} value={tab} onChange={setTab} />

      {tab === 'Overview' && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
              alignItems: 'start',
            }}
          >
            <Panel title="Quotation Summary">
              <KeyValueList
                rows={[
                  { label: 'Quotation Number', value: q.number },
                  { label: 'Customer', value: s.customer },
                  { label: 'Route', value: `${s.from.city} (${s.from.code}) → ${s.to.city} (${s.to.code})` },
                  { label: 'Transport Mode', value: s.service },
                  { label: 'Shipment Type', value: s.cargo },
                  { label: 'Incoterm', value: s.incoterm },
                  { label: 'Cargo Details', value: s.load },
                  { label: 'Quoted Price', value: moneyOrDash(q.price, q.currency), bold: true },
                  { label: 'Valid Until', value: q.validUntil },
                  { label: 'Created', value: q.created },
                  { label: 'Created By', value: 'SERB AI, for Ibraheem' },
                ]}
              />
            </Panel>
            <Stack spacing={2}>
              <NextActionCard q={q} />
              <Panel title="Internal Notes">
                {q.notes ? <Typography variant="body2">{q.notes}</Typography> : <EmptyState>No notes yet.</EmptyState>}
              </Panel>
            </Stack>
          </Box>
          <PriceOptions q={q} />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
              alignItems: 'start',
            }}
          >
            <RoutePanel route={d.route} />
            <CargoPanel packages={d.packages} />
            <PartiesPanel parties={d.parties} />
          </Box>
        </Stack>
      )}
      {tab === 'Prices & Options' && <PriceOptions q={q} />}
      {tab === 'Documents' && (
        <DocumentsPanel
          title="Quotation PDF"
          subtitle="Every quotation goes out as the SERB Word template saved as a PDF."
          records={docs.forShipment(q.srb).filter((r) => r.type === 'Customer Quotation')}
        />
      )}
      {(tab === 'Email Content' || tab === 'History') && (
        <Panel title={tab}>
          <EmptyState>This tab is coming soon.</EmptyState>
        </Panel>
      )}
    </Stack>
  );
}

/** What to do next. Nothing is sent from here: sending needs the server and your approval. */
function NextActionCard({ q }: { q: Quotation }) {
  const notice = useNotice();
  const next = NEXT_ACTION[q.status];
  return (
    <Panel title="Next Action">
      <Typography sx={{ fontWeight: 700 }}>{next.text}</Typography>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <StatusDot tone={next.tone} />
        <Typography variant="body2">{next.priority}</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', color: 'text.secondary' }}>
        <CalendarTodayOutlined fontSize="small" />
        <Typography variant="body2">Valid until {q.validUntil}</Typography>
      </Stack>
      {next.button && (
        <Button variant="contained" onClick={() => notice('This is not connected yet. Nothing was sent.', 'info')}>
          {next.button}
        </Button>
      )}
    </Panel>
  );
}

type OptionRow = { index: number; service: string; transit: string; cost: number; price: number };

/** The ways to ship it. The radio picks which price goes on the quotation. */
function PriceOptions({ q }: { q: Quotation }) {
  const [chosen, setChosen] = useState(0);
  const rows: OptionRow[] = q.options.map((o, index) => ({ index, ...o }));
  return (
    <Panel title="Price Options">
      <DataTable
        rows={rows}
        rowKey={(r) => String(r.index)}
        empty="No price options yet."
        columns={[
          { header: 'Option', primary: true, render: (r) => r.index + 1 },
          { header: 'Airline / Service', render: (r) => r.service },
          { header: 'Transit Time', nowrap: true, render: (r) => r.transit },
          { header: `Total Cost (${q.currency})`, render: (r) => r.cost.toLocaleString('en-US') },
          { header: 'Suggested Selling Price', render: (r) => r.price.toLocaleString('en-US') },
          {
            header: 'Expected Profit',
            render: (r) => (
              <Typography component="span" color="success.main" sx={{ fontWeight: 700 }}>
                {(r.price - r.cost).toLocaleString('en-US')}
              </Typography>
            ),
          },
          {
            header: 'Margin',
            render: (r) => (
              <Typography component="span" color="success.main" sx={{ fontWeight: 700 }}>
                {margin(r.price, r.cost)}
              </Typography>
            ),
          },
          {
            header: 'Default',
            action: true,
            render: (r) => (
              <Radio
                checked={chosen === r.index}
                onChange={() => setChosen(r.index)}
                slotProps={{ input: { 'aria-label': `Use option ${r.index + 1}` } }}
              />
            ),
          },
        ]}
      />
    </Panel>
  );
}
